import {
  ComponentPropsWithRef,
  Dispatch,
  SetStateAction,
  useEffect,
  useRef,
  useState,
} from "react";
import * as styles from "./BurgerMenu.css";
import gsap from "gsap";
import clsx from "clsx";
import { Button } from "../elements/Button";
// @ts-ignore
import MenuIcon from "@material-design-icons/svg/outlined/menu.svg?react";
// @ts-ignore
import CloseIcon from "@material-design-icons/svg/outlined/close.svg?react";
import { Transition } from "react-transition-group";
import * as Dialog from "@radix-ui/react-dialog";
import Grid from "./Grid";
import { Menu, Settings } from "~/sanity/types";
import { NavLink, useLocation } from "@remix-run/react";
import { MenuItem } from "../elements/MenuItem";
import { Paragraph } from "../elements/Paragraph";
import { Socials } from "../elements/Socials";

export type BurgerMenuProps = {
  data: Menu;
  setIsBurgerMenuOpen: Dispatch<SetStateAction<boolean>>;
  socials?: Settings["socials"];
} & ComponentPropsWithRef<"div">;

export const BurgerMenu = ({
  socials,
  data,
  setIsBurgerMenuOpen,
  className,
  ...props
}: BurgerMenuProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const nodeRef = useRef(null);
  const menuRef = useRef(null);
  const menuListRef = useRef<HTMLDivElement | null>(null);
  const location = useLocation();
  const { items } = data;

  const menuItemsEven = (items?.length || 0) % 2 === 0;

  useEffect(() => {
    setIsOpen(false);
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  useEffect(() => {
    if (isOpen) {
      setIsBurgerMenuOpen(true);
    }
  }, [isOpen, setIsBurgerMenuOpen]);

  const menuListChildren = menuListRef.current
    ? gsap.utils.toArray(menuListRef.current.children)
    : [];
  const handleEnter = () => {
    const enterTl = gsap.timeline();
    enterTl
      .to(menuRef.current, { display: "block", duration: 0 })
      .to(menuRef.current, {
        y: "0%",
        duration: 1,
        ease: "power3.out",
      })
      .fromTo(
        menuListChildren,
        { opacity: 0 },
        {
          opacity: 1,
          duration: 1,
          ease: "none",
          stagger: {
            amount: 1,
            grid: "auto",
            from: "start",
            ease: "none",
          },
        },
        "<+=0.2",
      );
  };

  const handleExit = () => {
    const exitTl = gsap.timeline();
    exitTl

      .to(
        menuRef.current,
        {
          duration: 1,
          ease: "power3.in",
          y: "-100%",
        },
        "<+=0.5",
      )
      .to(menuRef.current, {
        display: "none",
        duration: 0,
        onComplete: () => setIsBurgerMenuOpen(false),
      });
  };

  return (
    <div className={clsx(className)} {...props}>
      <Button
        onClick={() => setIsOpen(!isOpen)}
        icons={{ iconPrimary: isOpen ? <CloseIcon /> : <MenuIcon /> }}
        theme="light"
      ></Button>

      <Transition
        nodeRef={nodeRef}
        in={isOpen}
        timeout={1000}
        onEnter={handleEnter}
        onExit={handleExit}
      >
        {() => (
          <div ref={nodeRef} style={{ display: "contents" }}>
            <Dialog.Root open={isOpen} modal={false}>
              <Dialog.Portal forceMount>
                <Dialog.Title></Dialog.Title>
                <Dialog.Content
                  className={styles.menuContainer}
                  forceMount
                  aria-describedby={undefined}
                  onOpenAutoFocus={(e) => e.preventDefault()}
                  ref={menuRef}
                >
                  <Grid className={styles.grid}>
                    <div className={styles.menu}>
                      <div className={styles.menuList} ref={menuListRef}>
                        {items?.map((item) => {
                          const slug = (
                            item.link as unknown as
                              | { slug?: string }
                              | undefined
                          )?.slug;
                          if (!slug) return null;
                          return (
                            <div key={slug} className={styles.menuItem}>
                              <NavLink
                                to={slug}
                                onClick={() => setIsOpen(false)}
                                className={({ isActive }) =>
                                  styles.navLink({
                                    isActive: isActive,
                                  })
                                }
                              >
                                <MenuItem hasItems={false} isOpen={false}>
                                  <Paragraph size="uiBurgerMenu">
                                    {item.link?.text}
                                  </Paragraph>
                                </MenuItem>
                              </NavLink>
                            </div>
                          );
                        })}
                        {socials && (
                          <div
                            className={styles.socials({
                              menuItemsEven,
                            })}
                          >
                            <Socials
                              theme="dark"
                              isPrimary={true}
                              {...socials}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </Grid>
                </Dialog.Content>
              </Dialog.Portal>
            </Dialog.Root>
          </div>
        )}
      </Transition>
    </div>
  );
};
